// Copyright (c) Brock Allen & Dominick Baier. All rights reserved.
// Licensed under the Apache License, Version 2.0. See LICENSE in the project root for license information.

import { Logger, JwtUtils } from "./utils";
import { JsonService } from "./JsonService";
import type { MetadataService } from "./MetadataService";
import { SolaceCredentials, UserProfile } from "./User";
import type { JwtClaims } from "./Claims";
import type { UserManagerSettings } from "./UserManagerSettings";

/**
 * @internal
 */
export class UserInfoService {
    protected readonly _logger = new Logger("UserInfoService");
    private readonly _jsonService: JsonService;

    public constructor(private readonly _metadataService: MetadataService, private readonly _settings: UserManagerSettings) {
        this._jsonService = new JsonService(undefined);
    }

    public async getAccessToken(glnFedSsoTicket: string): Promise<string> {
        const logger = this._logger.create("getAccessToken");
        if (!glnFedSsoTicket) {
            this._logger.error("getClaims: No glnFedSsoTicket passed");
            throw new Error("A glnFedSsoTicket is required");
        }

        const url = this._settings.authority + '/tokens/introspect';

        logger.debug("got userinfo url", url);

        const introspectDto = await this._jsonService.postForm(url, { glnFedSsoTicket: glnFedSsoTicket });

        logger.debug("got access_token", introspectDto);

        return introspectDto.token as string;
    }

    public async getClaims(token: string): Promise<JwtClaims> {
        const logger = this._logger.create("getClaims");
        if (!token) {
            this._logger.throw(new Error("No token passed"));
        }
        
        const claims = await this._getClaimsFromJwt(token);

        logger.debug("got claims", claims);

        return claims;
    }

    public async getSolaceCredentials(token: string): Promise<SolaceCredentials> {
        const logger = this._logger.create("getSolaceCredentials");
        if (!token) {
            this._logger.throw(new Error("No token passed"));
        }

        const url = await this._metadataService.getSolaceCredentialsEndpoint()

        logger.debug("got credentials url", url);

        const credentials = await this._jsonService.getJson(url, { token: token });

        return new SolaceCredentials(credentials);
    }

    protected _getClaimsFromJwt = async (responseText: string): Promise<JwtClaims> => {
        const logger = this._logger.create("_getClaimsFromJwt");
        try {
            const payload = JwtUtils.decode(responseText);
            logger.debug("JWT decoding successful");

            return payload;
        } catch (err) {
            logger.error("Error parsing JWT response");
            throw err;
        }
    };
}